import { HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject, untracked } from '@angular/core';
import { Cart, ShoppingCartApi } from '@idealsupply/ngclient-webservice-shopping-cart';
import { patchState, Prettify, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { CurrentCustomerStore } from 'customers-data';
import { ItemId, stringifyItemId } from 'products-data';
import { catchError, debounceTime, map, of, retry, Subject, Subscription, switchMap, tap, timer } from 'rxjs';
import { EntityRecord, withCachedEntityStore } from 'state-data';
import { CART_TYPE } from '../tokens';
import { CartEntity } from '../types/CartEntity';
import { CartEntityItem } from '../types/CartEntityItem';
import { UnsavedCartQuantities } from './unsaved-cart-quantities';

export function cartId(cart: CartEntity | Cart): string {
  return `${cart.type}|${cart.customerNumber}`;
}

// Constants for timing configurations
const DEBOUNCE_MS = 400;
const RETRY_DELAYS_MS = [500, 1000, 2000] as const;
const RETRY_COUNT = 3;

/**
 * Events emitted by the cart store to track state changes.
 * All events include a timestamp for debugging and auditing.
 */
export type CartEvent =
  | { type: 'item-update'; cartId: string; itemId: ItemId; quantity: number; timestamp: number }
  | { type: 'item-update-saved'; cartId: string; itemId: ItemId; quantity: number; timestamp: number }
  | { type: 'item-update-failed'; cartId: string; itemId: ItemId; error: unknown; timestamp: number }
  | { type: 'clear-cart'; cartId: string; timestamp: number }
  | { type: 'clear-cart-saved'; cartId: string; timestamp: number }
  | { type: 'clear-cart-failed'; cartId: string; error: unknown; timestamp: number };

export const CartsStore = signalStore(
  { providedIn: 'root' },
  withState({
    _activeCartId: 'main',
    _activeCartRecord: null as EntityRecord<CartEntity> | null,
    _mainCartRecord: null as EntityRecord<CartEntity> | null,
  }),
  withCachedEntityStore({
    selectId: (c: CartEntity) => cartId(c),
    load: (cartId: string) => {
      const cartApi = inject(ShoppingCartApi);
      const unsaved = inject(UnsavedCartQuantities);
      const parts = cartId.split('|');
      const type = parts[0];
      const customerNumber = parts[1];
      const startedAt = Date.now();
      return cartApi.getCart(type, customerNumber).pipe(
        map(
          (cart) =>
            ({
              id: cart.id,
              items: cart.items
                .map((item) => {
                  const id = ItemId.parse(item.id);
                  return id
                    ? ({
                        id,
                        quantity: item.quantity,
                      } as CartEntityItem)
                    : undefined;
                })
                .filter((i) => !!i),
              customerNumber,
              type,
              created: new Date(cart.created),
              updated: new Date(cart.updated),
              settings: cart.settings,
            }) as CartEntity,
        ),
        // What the user entered wins over an older quantity from the API.
        map((cart) => unsaved.applyTo(cartId, cart, startedAt)),
      );
    },
  }),
  withMethods((store) => {
    // store.get()
    const setActiveCartId = (cartId: string) => {
      patchState(store, { _activeCartId: cartId || 'main' });
    };

    /**
     * Expires the cached copy of a cart and fetches it again from the API.
     * Used when the cart was changed outside this app (e.g. a vendor punchout
     * posting items into it) and the optimistic local state is stale.
     *
     * @param cId - Serialized cart ID (format: "type|customerNumber")
     */
    const reloadCart = (cId: string) => {
      patchState(store, (state) => {
        const current = state._entitiesEntityMap[cId];
        return current ? { _entitiesEntityMap: { ...state._entitiesEntityMap, [cId]: { ...current, lastUpdated: 0 } } } : {};
      });
      // get() triggers a load whenever the cache entry is expired
      store.get(cId);
    };

    return {
      setActiveCartId,
      reloadCart,
    };
  }),

  withComputed((store) => {
    const customerStore = inject(CurrentCustomerStore);
    const cartType = inject(CART_TYPE);

    const mainCartId = computed(() => `${cartType}|${customerStore.customerNumber()}`);

    const activeCartId = computed(() => {
      const activeId = store._activeCartId();
      return activeId === 'main' ? mainCartId() : activeId;
    });

    const mainCart = computed(() => store._mainCartRecord()?.entity ?? null);
    const mainCartLoading = computed(() => store._mainCartRecord()?.loading ?? false);
    const mainCartError = computed(() => store._mainCartRecord()?.error ?? false);

    const activeCart = computed(() => store._activeCartRecord()?.entity ?? null);
    const activeCartLoading = computed(() => store._activeCartRecord()?.loading ?? false);
    const activeCartError = computed(() => store._activeCartRecord()?.error ?? false);

    return {
      mainCartId,
      mainCart,
      mainCartLoading,
      mainCartError,
      activeCartId,
      activeCart,
      activeCartLoading,
      activeCartError,
    };
  }),
  withState({
    pendingItems: {} as Record<string, Set<string>>,
    clearQueues: {} as Record<string, { itemId: ItemId; quantity: number }[]>,
  }),
  withProps(() => {
    const eventsSubject = new Subject<CartEvent>();
    const itemSubjects = new Map<string, Map<string, Subject<number>>>();
    const itemSubscriptions = new Map<string, Map<string, Subscription>>();
    return {
      events$: eventsSubject.asObservable(),
      _eventsSubject: eventsSubject,
      _itemSubjects: itemSubjects,
      _itemSubscriptions: itemSubscriptions,
    };
  }),
  withMethods((store) => {
    const cartApi = inject(ShoppingCartApi);
    const unsaved = inject(UnsavedCartQuantities);
    // store.
    // Helper to remove item from pending state immutably
    // Safe to call even if cId doesn't exist - creates empty Set
    const removePending = (cId: string, itemIdStr: string) => {
      patchState(store, (state) => {
        const pending = new Set(state.pendingItems[cId]);
        pending.delete(itemIdStr);
        return { pendingItems: { ...state.pendingItems, [cId]: pending } };
      });
    };

    // Helper to check if error is temporary (should retry)
    const isTemporaryError = (error: unknown): boolean => {
      if (error instanceof HttpErrorResponse) {
        const status = error.status;
        // Retry on network errors (0) or 5xx server errors
        return status === 0 || (status >= 500 && status < 600);
      }
      // Retry on non-HTTP errors (network issues)
      return true;
    };

    /** Last quantity per item from a clear queue. */
    const mergeQueue = (queue: { itemId: ItemId; quantity: number }[]) => {
      const merged = new Map<string, { itemId: ItemId; quantity: number }>();
      queue.forEach((update) => merged.set(stringifyItemId(update.itemId), update));
      return merged;
    };

    /**
     * Updates the quantity of an item in the cart with optimistic updates and debouncing.
     *
     * @param cId - Serialized cart ID (format: "type|customerNumber")
     * @param itemId - Item identifier with lineCode and partNumber
     * @param quantity - New quantity (negative values normalized to 0, which deletes the item)
     *
     * Features:
     * - Optimistic UI updates (immediate feedback)
     * - Per-item debouncing (400ms) to batch rapid changes
     * - Automatic retry with exponential backoff for transient errors
     * - Queue management during cart clear operations
     * - Event emissions for tracking state changes
     */
    const updateItemQuantity = (cId: string, itemId: ItemId, quantity: number) => {
      // Normalize quantity (negative -> 0)
      const normalizedQty = Math.max(0, quantity);
      const itemIdStr = stringifyItemId(itemId);

      // Get cart from store
      const cartSignal = untracked(store.get(cId));
      const cart = cartSignal.entity;
      if (!cart) return;

      // 1. Optimistic update to store
      const currentItem = cart.items.find((i) => i.id.equals(itemId));

      // Skip if quantity hasn't changed
      if (currentItem && currentItem.quantity === normalizedQty) {
        return;
      }

      // Skip if trying to delete a non-existent item
      if (!currentItem && normalizedQty === 0) {
        return;
      }

      let updatedItems: CartEntityItem[];
      if (!currentItem && normalizedQty > 0) {
        // Adding new item
        updatedItems = [...cart.items, { id: itemId, quantity: normalizedQty }];
      } else if (currentItem && normalizedQty === 0) {
        // Deleting item - keep in store with qty 0 until API confirms
        updatedItems = cart.items.map((i) => (currentItem === i ? { ...i, quantity: 0 } : i));
      } else if (currentItem) {
        // Updating quantity
        updatedItems = cart.items.map((i) => (currentItem === i ? { ...i, quantity: normalizedQty } : i));
      } else {
        // No change needed
        return;
      }

      store.setData({ ...cart, items: updatedItems });
      unsaved.set(cId, { id: itemId, quantity: normalizedQty });

      // 2. Mark item as pending
      patchState(store, (state) => {
        const pending = new Set(state.pendingItems[cId] ?? []);
        pending.add(itemIdStr);
        return { pendingItems: { ...state.pendingItems, [cId]: pending } };
      });

      // 3. Emit optimistic event
      store._eventsSubject.next({ type: 'item-update', cartId: cId, itemId, quantity: normalizedQty, timestamp: Date.now() });

      // 4. Check if cart is clearing - if so, queue this update
      const clearQueue = store.clearQueues()[cId];
      if (clearQueue) {
        patchState(store, (state) => ({
          clearQueues: {
            ...state.clearQueues,
            [cId]: [...clearQueue, { itemId, quantity: normalizedQty }],
          },
        }));
        return;
      }

      // 5. Send it (debounced per item)
      save(cId, itemId, normalizedQty);
    };

    /**
     * Sends an item's quantity to the API: per-item debounce, retry for transient errors,
     * then confirms it (see `UnsavedCartQuantities`).
     */
    const save = (cId: string, itemId: ItemId, quantity: number) => {
      const itemIdStr = stringifyItemId(itemId);

      // Get or create Subject for this item
      let cartSubjects = store._itemSubjects.get(cId);
      if (!cartSubjects) {
        cartSubjects = new Map<string, Subject<number>>();
        store._itemSubjects.set(cId, cartSubjects);
      }

      let itemSubject = cartSubjects.get(itemIdStr);
      if (!itemSubject) {
        itemSubject = new Subject<number>();
        cartSubjects.set(itemIdStr, itemSubject);

        // Track subscriptions for cleanup
        let cartSubscriptions = store._itemSubscriptions.get(cId);
        if (!cartSubscriptions) {
          cartSubscriptions = new Map<string, Subscription>();
          store._itemSubscriptions.set(cId, cartSubscriptions);
        }

        // Set up the pipeline for this item (only once)
        const subscription = itemSubject
          .pipe(
            debounceTime(DEBOUNCE_MS),
            switchMap((qty) => {
              const parts = cId.split('|');
              const type = parts[0];
              const customerNumber = parts[1];

              return cartApi.setCartItemQuantity(type, customerNumber, { id: itemId.toString(true), quantity: qty }).pipe(
                retry({
                  count: RETRY_COUNT,
                  delay: (error, retryCount) => {
                    if (!isTemporaryError(error)) {
                      throw error;
                    }
                    const delayMs = RETRY_DELAYS_MS[retryCount - 1] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
                    return timer(delayMs);
                  },
                }),
                tap(() => {
                  // A newer quantity is still on its way: the item stays pending (and in the cart) for that one.
                  if (unsaved.confirm(cId, itemIdStr, qty)) {
                    removePending(cId, itemIdStr);

                    if (qty === 0) {
                      const currentCart = untracked(store.get(cId))?.entity as CartEntity | null;
                      if (currentCart) {
                        const filteredItems = currentCart.items.filter((i) => !i.id.equals(itemId));
                        store.setData({ ...currentCart, items: filteredItems });
                      }

                      const cartSubs = store._itemSubscriptions.get(cId);
                      const sub = cartSubs?.get(itemIdStr);
                      if (sub) {
                        sub.unsubscribe();
                        cartSubs?.delete(itemIdStr);
                      }
                      store._itemSubjects.get(cId)?.delete(itemIdStr);
                    }
                  }

                  store._eventsSubject.next({ type: 'item-update-saved', cartId: cId, itemId, quantity: qty, timestamp: Date.now() });
                }),
                catchError((error) => {
                  try {
                    if (unsaved.isLatest(cId, itemIdStr, qty)) {
                      unsaved.delete(cId, itemIdStr);
                      removePending(cId, itemIdStr);
                    }

                    store._eventsSubject.next({ type: 'item-update-failed', cartId: cId, itemId, error, timestamp: Date.now() });
                  } catch (e) {
                    console.error('Error in cart update error handler:', e);
                  }

                  return of(null);
                }),
              );
            }),
          )
          .subscribe({
            error: (err) => {
              console.error('Unhandled cart subscription error:', err);
            },
          });

        cartSubscriptions.set(itemIdStr, subscription);
      }

      // Push quantity to Subject (triggers debounce -> API call)
      itemSubject.next(quantity);
    };

    /**
     * Clears all items from the cart with optimistic updates and queue management.
     *
     * @param cId - Serialized cart ID (format: "type|customerNumber")
     *
     * Features:
     * - Cancels all pending/in-flight item updates
     * - Optimistic UI updates (immediate empty cart)
     * - Completes all item subscriptions to cancel in-flight requests
     * - Queues any updates that arrive during the clear operation
     * - Processes queued updates after successful clear (merged by itemId, last-write-wins)
     * - Automatic retry with exponential backoff for transient errors
     * - Event emissions for tracking state changes
     */
    const clearCart = (cId: string) => {
      // Get cart from store
      const cartSignal = untracked(store.get(cId));
      const cart = cartSignal.entity;
      if (!cart || !cart.type || !cart.customerNumber) {
        return;
      }

      // Step 1: Clear all pending items (cancels pending updates)
      unsaved.clear(cId);
      patchState(store, (state) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [cId]: _removed, ...remainingPending } = state.pendingItems;
        return { pendingItems: remainingPending };
      });

      // Step 2: Optimistic update - set items to empty array
      const emptyCart: CartEntity = {
        ...cart,
        items: [],
      };
      store.setData(emptyCart, true);

      // Step 3: Emit optimistic clear-cart event
      store._eventsSubject.next({
        type: 'clear-cart',
        cartId: cId,
        timestamp: Date.now(),
      });

      // Step 4: Complete all item Subjects and cleanup subscriptions (cancels in-flight requests)
      const itemSubscriptions = store._itemSubscriptions.get(cId);
      if (itemSubscriptions) {
        itemSubscriptions.forEach((subscription) => {
          subscription.unsubscribe();
        });
        store._itemSubscriptions.delete(cId);
      }

      const itemSubjects = store._itemSubjects.get(cId);
      if (itemSubjects) {
        itemSubjects.forEach((subject) => {
          subject.complete();
        });
        store._itemSubjects.delete(cId);
      }

      // Step 5: Initialize queue for updates that arrive during clear
      if (!store.clearQueues()[cId]) {
        patchState(store, {
          clearQueues: {
            ...store.clearQueues(),
            [cId]: [],
          },
        });
      }

      // Step 6: Call API with retry logic
      cartApi
        .emptyCart(cart.type, cart.customerNumber)
        .pipe(
          retry({
            count: RETRY_COUNT,
            delay: (error, retryCount) => {
              if (!isTemporaryError(error)) {
                throw error; // Don't retry client errors
              }
              // Exponential backoff using configured delays
              const delayMs = RETRY_DELAYS_MS[retryCount - 1] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
              return timer(delayMs);
            },
          }),
        )
        .subscribe({
          next: () => {
            // Step 7: Success - emit clear-cart-saved event
            store._eventsSubject.next({
              type: 'clear-cart-saved',
              cartId: cId,
              timestamp: Date.now(),
            });

            // Step 8: Process queued updates
            const queue = store.clearQueues()[cId] || [];
            const queueCopy = [...queue];

            // Clear the queue
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [cId]: _removed, ...remainingQueues } = store.clearQueues();
            patchState(store, {
              clearQueues: remainingQueues,
            });

            // Send what the user set during the clear (last write per item wins). The cart already
            // shows these quantities, so they go straight to save(): updateItemQuantity() would see
            // no change and skip them. A 0 needs nothing sent (the clear removed the item).
            mergeQueue(queueCopy).forEach(({ itemId, quantity }, itemIdStr) => {
              if (quantity > 0) {
                save(cId, itemId, quantity);
              } else {
                const currentCart = untracked(store.get(cId))?.entity as CartEntity | null;
                if (currentCart) {
                  store.setData({ ...currentCart, items: currentCart.items.filter((i) => !i.id.equals(itemId)) });
                }
                unsaved.delete(cId, itemIdStr);
                removePending(cId, itemIdStr);
              }
            });
          },
          error: (error: unknown) => {
            // Step 9: Error - emit clear-cart-failed event
            store._eventsSubject.next({
              type: 'clear-cart-failed',
              cartId: cId,
              error,
              timestamp: Date.now(),
            });

            // Clear the queue
            const queueCopy = [...(store.clearQueues()[cId] || [])];
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [cId]: _removed, ...remainingQueues } = store.clearQueues();

            // The cart wasn't cleared, so everything the user set during the clear still has to be sent
            mergeQueue(queueCopy).forEach(({ itemId, quantity }) => save(cId, itemId, quantity));

            // Invalidate cache by setting lastUpdated to 0
            patchState(store, (state) => {
              const currentEntity = state._entitiesEntityMap[cId];
              return {
                clearQueues: remainingQueues,
                ...(currentEntity
                  ? {
                      _entitiesEntityMap: {
                        ...state._entitiesEntityMap,
                        [cId]: { ...currentEntity, lastUpdated: 0 },
                      },
                    }
                  : {}),
              };
            });
          },
        });
    };

    /**
     * Returns a computed signal containing pending item IDs for a cart.
     *
     * @param cId - Serialized cart ID
     * @returns Computed signal with array of pending item ID strings
     */
    const getPendingItems = (cId: string) => {
      return computed(() => Array.from(store.pendingItems()[cId] ?? []));
    };

    /**
     * Checks if a specific item has a pending update.
     *
     * @param cId - Serialized cart ID
     * @param itemId - Item identifier to check
     * @returns True if item has pending update, false otherwise
     */
    const isItemPending = (cId: string, itemId: ItemId): boolean => {
      const itemIdStr = stringifyItemId(itemId);
      return store.pendingItems()[cId]?.has(itemIdStr) ?? false;
    };

    return {
      updateItemQuantity,
      clearCart,
      isItemPending,
      getPendingItems,
    };
  }),
  withHooks((store) => {
    return {
      onInit() {
        effect(() => {
          const mainCart = store.get(store.mainCartId());
          patchState(store, { _mainCartRecord: mainCart() });
        });
        effect(() => {
          const activeCart = store.get(store.activeCartId());
          patchState(store, { _activeCartRecord: activeCart() });
        });
      },
    };
  }),
);

export type CartsStore = Prettify<InstanceType<typeof CartsStore>>;
